import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isBelowMinBundle } from "@/lib/minVersion";
import { forceReloadToLatest } from "@/lib/appUpdate";
import { applyPendingCapgoUpdate, isWorkoutInProgress } from "@/lib/seamlessUpdate";

// The running OTA bundle semver, baked in at build time from package.json.
// Empty in dev (no injected version) — the gate never fires without it.
const APP_VERSION: string =
  typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "";

interface MobileVersions {
  min_bundle?: string | null;
}

/**
 * The one hard floor in the update system.
 *
 * Every other update surface (Capgo auto-update, the update pill, the App
 * Store banner) is a soft nudge. This is the only thing that can *require*
 * a user to move: when the backend serves a `min_bundle` on
 * /api/public/mobile-version that is strictly newer than the running
 * bundle, this shows a full-screen, non-dismissible "please update" screen.
 *
 * It exists for one scenario: a backend change has shipped that an older
 * bundle can't talk to (the backend deploys the moment a PR merges, but
 * the matching bundle reaches phones separately via Capgo — they can
 * drift). Raising `min_bundle` by hand after such a change gates stranded
 * users instead of leaving them silently broken.
 *
 * Dormant by default: `min_bundle` is `null`, so this renders nothing on
 * every normal release. And it FAILS OPEN — `isBelowMinBundle` returns
 * false for a null/absent/garbage floor or an unknown running version, and
 * the query error/loading states leave `data` undefined — so a network
 * blip or a bad read can never lock a working app out.
 */
export default function MinimumVersionGate() {
  const { data } = useQuery<MobileVersions>({
    queryKey: ["/api/public/mobile-version"],
    // Re-check on focus and periodically so LOWERING the floor (or a
    // fix landing) releases gated users without a manual restart, and
    // so RAISING it catches a long-open session reasonably soon.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
  });

  const [working, setWorking] = useState(false);
  const [needsRestart, setNeedsRestart] = useState(false);

  if (!isBelowMinBundle(APP_VERSION, data?.min_bundle)) return null;

  const onUpdate = async () => {
    if (working) return;
    // Never reload a bundle out from under a live workout, even here —
    // the user should save/finish first (founder lost a session to a
    // mid-workout reload, 2026-07-03). The gate stays up; they update
    // the moment the workout ends.
    if (isWorkoutInProgress()) {
      window.alert(
        "You have a workout in progress — please finish and save it first, then update.",
      );
      return;
    }
    setWorking(true);
    try {
      if (Capacitor.isNativePlatform()) {
        // Tries in-memory → on-disk → manual download, then reloads.
        const didApply = await applyPendingCapgoUpdate();
        if (!didApply) {
          // Bundle wasn't ready to swap in-process. Capgo's autoUpdate
          // pulls and applies the channel-latest on the next cold launch,
          // so a full close-and-reopen is the reliable finish.
          setNeedsRestart(true);
          setWorking(false);
        }
        // On success the webview tears down within a second; nothing else
        // to do.
      } else {
        await forceReloadToLatest();
      }
    } catch {
      setNeedsRestart(true);
      setWorking(false);
    }
  };

  return (
    <div
      // z-[300] sits above the update pill (z-200) and everything else —
      // this is a blocking screen, not a toast.
      className="fixed inset-0 z-[300] flex items-center justify-center bg-background/95 backdrop-blur-sm p-6"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="min-version-title"
    >
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 text-center shadow-lg">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/15">
          <RefreshCw className="h-6 w-6 text-primary" />
        </div>
        <h2
          id="min-version-title"
          className="text-lg font-semibold text-foreground"
        >
          Time to update TrybeUP
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          This version is out of date and some things may not work correctly.
          Update now to keep going — it only takes a moment.
        </p>

        {needsRestart ? (
          <p className="mt-5 rounded-xl bg-muted/60 p-3 text-sm text-foreground">
            Almost there — please fully close the app (swipe it away) and open
            it again to finish updating.
          </p>
        ) : (
          <Button
            onClick={onUpdate}
            disabled={working}
            className="mt-5 w-full rounded-full"
          >
            {working ? "Updating…" : "Update now"}
          </Button>
        )}
      </div>
    </div>
  );
}
